import { action, computed, observable, runInAction } from 'mobx';
import type { SortingObservable } from '../../components/Grid/Grid';
import { GridColDef } from '../../components/Grid/Grid';
import AbstractStore from '../../store/AbstractStore';
import type { RoleApi } from '../../types/users-types';
import { sortByField } from '../../utils/object-utils';
import { RoleDefinitionRenderer } from './renderers';

interface Loaders {
  page?: boolean;
}

export default class RolesManagementStore extends AbstractStore {
  @observable loaders: Loaders = {};

  @observable sorting: SortingObservable<RoleApi> = {};

  @observable rolesData: RoleApi[] | null = null;

  @computed
  public get rolesFiltered(): RoleApi[] {
    let filtered = this.rolesData || [];
    if (this.sorting.field) {
      filtered = sortByField(filtered, this.sorting.field);
      if (this.sorting.order === 'DESC') {
        filtered = filtered.reverse();
      }
    }
    return filtered;
  }

  @computed
  public get colDefs(): GridColDef<RoleApi>[] {
    return [
      { headerName: 'Name', field: 'name', sortable: true, renderer: RoleDefinitionRenderer },
      { headerName: 'Description', field: 'description', sortable: true },
      { headerName: 'Level', field: 'level', sortable: true },
      {
        headerName: 'Global',
        field: 'global',
        sortable: true,
        renderValue: props => (props.row.global ? 'Yes' : 'No'),
      },
      {
        headerName: 'Default',
        field: 'default',
        sortable: true,
        renderValue: props => (props.row.default ? 'Yes' : 'No'),
      },
    ];
  }

  @action
  public async init(): Promise<void> {
    runInAction(() => (this.loaders.page = true));
    if (!this.rolesData) {
      await this.fetchRoles();
    }
    runInAction(() => (this.loaders.page = false));
  }

  @action
  public close(): void {
    this.sorting = {};
  }

  @action
  public async fetchRoles(): Promise<void> {
    try {
      const roles = await this.root.apiStore.findAllRoles();
      runInAction(() => (this.rolesData = roles));
    } catch {
      runInAction(() => (this.rolesData = []));
    }
  }

  @action
  public showRolePermissions(row: RoleApi): void {
    this.root.roleManagementStore.roleName = row.name;
    this.navigate('/admin/role-management');
  }
}
